import { endOfDay, subDays } from 'date-fns';
import type { IntlShape } from 'react-intl';

import { Field } from '~/components/Shared/Filters_DEPRECATED/types';
import { Filter } from '~/evifields/src/types/Filter';

import { EntityType, Operation } from './types';

const getEntityTypeOptions = (
  selectedOperation: Operation | undefined,
  entityOptions: {
    value: EntityType;
    label: string;
  }[],
) => {
  const matchEntity = (entity: EntityType) => {
    const filteredEntity = entityOptions.filter(
      (option) => option.value === entity,
    );
    return filteredEntity;
  };

  switch (selectedOperation) {
    case 'DEACTIVATE':
    case 'EDIT':
    case 'LOGOUT':
    case 'LOGIN':
      return matchEntity('USER' as EntityType);
    case 'DELETE':
    case 'DOWNLOAD':
    case 'VIEW':
    case 'FIELD_CHANGE':
    case 'TERMINATE':
      return matchEntity('DOCUMENT' as EntityType);
    case 'SECURITY':
      return matchEntity('FOLDER' as EntityType);
    case 'CREATE':
    default:
      return entityOptions;
  }
};

export const validateFilters = (
  filters: Filter[],
  entityOptions: {
    value: EntityType;
    label: string;
  }[],
) => {
  const [operationFilter, entityFilter, ...restFilters] = filters;

  const operation = operationFilter.values[0] as Operation;
  const entityValue = entityFilter.values[0];

  const entities = getEntityTypeOptions(operation, entityOptions);
  const validEntityValues = entities.map((entity) => entity.value);
  const isValidEntity = validEntityValues.some(
    (validEntityValue) => validEntityValue === entityValue,
  );

  return isValidEntity
    ? filters
    : [
        operationFilter,
        { ...entityFilter, values: validEntityValues },
        ...restFilters,
      ];
};

export const getFields = ({
  selectedOperation,
  intl,
  operationOptions,
  entityOptions,
}: {
  selectedOperation?: Operation;
  intl: IntlShape;
  operationOptions: {
    value: Operation;
    label: string;
  }[];
  entityOptions: {
    value: EntityType;
    label: string;
  }[];
}): Record<string, Field> => {
  const today = new Date();
  const max = endOfDay(today);
  const min = subDays(today, 179);

  return {
    activity: {
      id: 'activity',
      label: intl.formatMessage({
        id: 'AuditLogs.activityTypeLabel',
        defaultMessage: 'Activity Type',
        description: 'Label for activity type filter field',
      }),
      type: 'enum',
      whitelistOperatorIds: ['equals'],
      settings: {
        options: operationOptions,
      },
    },
    entityType: {
      id: 'entityType',
      label: intl.formatMessage({
        id: 'AuditLogs.recordTypeLabel',
        defaultMessage: 'Record Type',
        description: 'Label for record type filter field',
      }),
      type: 'enum',
      whitelistOperatorIds: ['equals'],
      settings: {
        options: getEntityTypeOptions(selectedOperation, entityOptions),
      },
    },
    datetime: {
      id: 'datetime',
      label: intl.formatMessage({
        id: 'AuditLogs.dateRangeLabel',
        defaultMessage: 'Date Range',
        description: 'Label for date range filter field',
      }),
      type: 'date',
      whitelistOperatorIds: [
        'date_between',
        'date_on',
        'date_after',
        'date_before',
      ],
      settings: {
        max,
        min,
        getDayTooltip: (date: Date) => {
          if (date > max) {
            return intl.formatMessage({
              id: 'AuditLogs.futureDateTooltip',
              defaultMessage: 'You can not select a future date',
              description: 'Tooltip for selecting a date in the future',
            });
          } else if (date < min) {
            return intl.formatMessage({
              id: 'AuditLogs.pastDateTooltip',
              defaultMessage: 'You can not go back more than 180 days',
              description:
                'Tooltip for selecting a date more than 180 days in the past',
            });
          }
        },
      },
    },
  };
};
